/**
 * Structural badge slots. ISSUE-326 owns the counts.
 *
 * A counted badge owns its own pluralization (ISSUE-773). The caller passes
 * both the singular and the plural noun phrase. This component picks the
 * one that agrees with `count` when it builds the `sr-only` text. A
 * dot-only badge has no numeral to agree with, so it takes one fixed label.
 */
import type { ReactNode } from "react";

/**
 * American English only, no i18n library. Matches the established local
 * pattern in reviveSheetModel.ts and reconcileCycleEntries.ts. Both forms
 * are passed in explicitly, never derived. A suffix rule like `+s` would
 * silently mis-pluralize an irregular noun a future caller adds.
 */
function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

export type typeNavBadgeProps =
  | {
      count?: number | undefined;
      doShowDot?: undefined;
      /** Read at a count of 1, e.g. "unread notification". */
      singularLabel: string;
      /** Read at any other count, e.g. "unread notifications". */
      pluralLabel: string;
    }
  | {
      count?: undefined;
      doShowDot?: boolean | undefined;
      /** Fixed text; there is no numeral to agree with. */
      label: string;
    };

export const NavBadge = (props: typeNavBadgeProps): ReactNode => {
  if ("singularLabel" in props) {
    const { count, singularLabel, pluralLabel } = props;
    if (count !== undefined && count > 0) {
      const countLabel = pluralize(count, singularLabel, pluralLabel);
      return (
        <span className="sm-nav-badge ml-1 inline-flex h-icon-md min-w-icon-md items-center justify-center rounded-full bg-primary-solid px-1 text-caption text-on-primary">
          {count > 99 ? "99+" : String(count)}
          <span className="sr-only"> {countLabel}</span>
        </span>
      );
    }
    return null;
  }

  const { doShowDot, label } = props;
  if (doShowDot === true) {
    return (
      <>
        <span
          className="ml-1 size-2 shrink-0 rounded-full bg-primary-solid"
          aria-hidden="true"
        />
        <span className="sr-only">{label}</span>
      </>
    );
  }

  return null;
};
